import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import { tradesAPI } from "@/services/api";
import { AcceptedQuote } from "@/types/quotes";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/shadcn/dialog";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import { AlertCircle, DollarSign, Clock, User, Calendar } from "lucide-react";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";
import { formatAssetAmount } from "@/lib/utils";

interface AddTradeExecutionDialogProps {
  quote: AcceptedQuote;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onTradeExecuted: () => void;
}

const AddTradeExecutionDialog = ({
  quote,
  isOpen,
  onOpenChange,
  onTradeExecuted,
}: AddTradeExecutionDialogProps) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [executionPrice, setExecutionPrice] = useState<string>("");
  const [executionDate, setExecutionDate] = useState<string>(
    DateTime.now().toISO() || ""
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    price?: string;
    date?: string;
    user?: string;
  }>({});

  // Reset form state when dialog opens
  useEffect(() => {
    if (isOpen) {
      // Initialize with current UTC time
      setExecutionDate(DateTime.utc().toISO() || "");
      setError(null);
      setValidationErrors({});
    }
  }, [isOpen]);

  const handleSetCurrentDateTime = () => {
    // Set to current UTC time
    setExecutionDate(DateTime.utc().toISO() || "");
    // Clear date validation error if it exists
    if (validationErrors.date) {
      setValidationErrors((prev) => ({ ...prev, date: undefined }));
    }
  };

  const validateInputs = () => {
    const errors: { price?: string; date?: string; user?: string } = {};
    let isValid = true;

    // Validate execution price
    if (!executionPrice || Number(executionPrice) <= 0) {
      errors.price = "Please enter a valid execution price";
      isValid = false;
    }

    // Validate execution date
    if (!executionDate) {
      errors.date = "Please select an execution date";
      isValid = false;
    } else {
      const execDate = DateTime.fromISO(executionDate);
      if (!execDate.isValid) {
        errors.date = "Invalid date format";
        isValid = false;
      }
    }

    // Validate user
    if (!user?.id) {
      errors.user = "User information not available";
      isValid = false;
    }

    setValidationErrors(errors);
    return isValid;
  };

  const handleAddExecution = async () => {
    // Clear previous errors
    setError(null);

    // Validate inputs
    if (!validateInputs()) {
      // Display a toast for the first validation error
      const firstError =
        validationErrors.price ||
        validationErrors.date ||
        validationErrors.user;
      if (firstError) {
        toast({
          title: "Validation Error",
          description: firstError,
          variant: "destructive",
        });
      }
      return;
    }

    setLoading(true);
    try {
      const tradeResult = await tradesAPI.createTrade({
        accepted_quote_id: quote.id,
        execution_price: executionPrice,
        executed_by_id: user!.id,
        executed_at: executionDate,
      });

      showSuccessToast(toast, "Success", "Trade executed successfully");

      if (import.meta.env.DEV) {
        console.log("Trade executed successfully:", tradeResult);
      }

      onTradeExecuted();
      onOpenChange(false);
    } catch (error) {
      handleApiError("Execute Trade", error, toast);
      setError(
        "Failed to execute trade. Please check all values and try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Compute fromAmount, toAmount, fromAsset, toAsset as in AcceptedQuotesTab
  const isBuy = quote.side === "buy";
  // For BUY:
  //   - total_from is always in counter (MXN)
  //   - total_to is always in base (BTC)
  // For SELL:
  //   - total_from is always in base (BTC)
  //   - total_to is always in counter (MXN)
  const fromAmount = isBuy ? quote.total_from : quote.total_from;
  const toAmount = isBuy ? quote.total_to : quote.total_to;
  // For BUY: fromAsset is counter (MXN), toAsset is base (BTC)
  // For SELL: fromAsset is base (BTC), toAsset is counter (MXN)
  const fromAsset = isBuy ? quote.vmarket.counter : quote.vmarket.base;
  const toAsset = isBuy ? quote.vmarket.base : quote.vmarket.counter;

  // Dynamically determine max decimals for price input
  const maxDecimals = quote?.vmarket?.counter?.contract_decimals ?? 18;

  const handleExecutionPriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    if (value.includes(".")) {
      const [intPart, decPart] = value.split(".");
      if (decPart.length > maxDecimals) {
        value = `${intPart}.${decPart.slice(0, maxDecimals)}`;
      }
    }
    setExecutionPrice(value);
    if (validationErrors.price) {
      setValidationErrors((prev) => ({
        ...prev,
        price: undefined,
      }));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px] p-0 overflow-hidden rounded-lg shadow-xl border-0">
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 py-5 px-6">
          <DialogHeader className="pb-0">
            <DialogTitle className="text-xl font-bold text-gray-800">
              Execute Trade
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-600 mt-1">
              Complete the execution details for this accepted quote
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Quote Summary Panel */}
        <div className="bg-white p-5 border-y border-gray-100">
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-blue-600" />
            Quote Summary
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-gray-500 text-xs mb-1">Client</p>
              <p className="font-medium text-gray-800">{quote.client.name}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-gray-500 text-xs mb-1">From</p>
              <p className="font-medium text-gray-800">
                {`${formatAssetAmount(fromAmount, fromAsset)} ${fromAsset.code}`}
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-gray-500 text-xs mb-1">Quoted Price</p>
              <p className="font-medium text-gray-800">
                {`${formatAssetAmount(quote.price || 0, quote.vmarket.counter)} ${quote.vmarket.counter.code}`}
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-gray-500 text-xs mb-1">To</p>
              <p className="font-medium text-gray-800">
                {`${formatAssetAmount(toAmount, toAsset)} ${toAsset.code}`}
              </p>
            </div>
          </div>
        </div>

        <div className="p-5">
          {/* General error message */}
          {error && (
            <div className="flex items-center space-x-2 bg-red-50 border border-red-100 rounded-md p-3 mb-4">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <p className="text-red-600 text-sm font-medium">{error}</p>
            </div>
          )}

          <div className="space-y-4">
            {/* Execution price */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <DollarSign className="w-4 h-4 mr-1 text-blue-600" />
                Execution Price ({quote.vmarket.counter.code})
                {validationErrors.price && (
                  <span className="text-red-500 ml-1 text-xs">*</span>
                )}
              </label>
              <div className="relative">
                <Input
                  type="number"
                  value={executionPrice}
                  onChange={handleExecutionPriceChange}
                  placeholder="Enter price"
                  className={`pl-2 ${
                    validationErrors.price
                      ? "border-red-300 focus-visible:ring-red-200"
                      : "border-gray-200 focus-visible:ring-blue-200"
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Up to {maxDecimals} decimal places allowed
                </p>
              </div>
              {validationErrors.price && (
                <p className="text-xs text-red-500 mt-1">
                  {validationErrors.price}
                </p>
              )}
            </div>

            {/* Execution date */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <Clock className="w-4 h-4 mr-1 text-blue-600" />
                Execution Date & Time
                {validationErrors.date && (
                  <span className="text-red-500 ml-1 text-xs">*</span>
                )}
              </label>
              <div className="relative w-full">
                <Input
                  type="datetime-local"
                  value={
                    executionDate
                      ? DateTime.fromISO(executionDate)
                          .toUTC()
                          .toFormat("yyyy-MM-dd'T'HH:mm")
                      : ""
                  }
                  onChange={(e) => {
                    // Convert to UTC ISO string with explicit timezone
                    const localDate = DateTime.fromISO(e.target.value);
                    const utcDate = localDate.toUTC();
                    const isoString = utcDate.toISO();
                    if (isoString) {
                      setExecutionDate(isoString);
                      if (validationErrors.date) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          date: undefined,
                        }));
                      }
                    }
                  }}
                  className={`w-full [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-3 [&::-webkit-calendar-picker-indicator]:py-2 ${
                    validationErrors.date
                      ? "border-red-300 focus-visible:ring-red-200"
                      : "border-gray-200 focus-visible:ring-blue-200"
                  }`}
                />
                <div className="absolute right-0 top-full mt-1 text-xs text-gray-500">
                  UTC Time
                </div>
              </div>
              {validationErrors.date && (
                <p className="text-xs text-red-500 mt-1">
                  {validationErrors.date}
                </p>
              )}
            </div>

            {/* Executed by */}
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <User className="w-4 h-4 mr-1 text-blue-600" />
                Executed By
              </label>
              <div className="text-sm py-2.5 px-3 bg-gray-50 rounded-md border border-gray-200 text-gray-800">
                {user?.email || "Loading user information..."}
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <DialogFooter className="bg-gray-50 px-5 py-4 border-t border-gray-100">
          <div className="flex justify-end gap-3 w-full">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="w-full sm:w-auto border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddExecution}
              disabled={loading}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
            >
              {loading ? "Processing..." : "Execute Trade"}
            </Button>
          </div>
        </DialogFooter>

        {/* Remove number input steppers for this component only */}
        <style>
          {`
            input[type=number]::-webkit-outer-spin-button,
            input[type=number]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
            input[type=number] {
              -moz-appearance: textfield;
            }
          `}
        </style>
      </DialogContent>
    </Dialog>
  );
};

export default AddTradeExecutionDialog;
